import {createContext} from 'react'

import {OrderDto} from '../../types'
import {OrderCancellationRequestPayload} from '../orderCancellationRequest/types'

/**
 * Discriminated union representing the timer state for order cancellation.
 * When timer is active, provides remaining time breakdown.
 * Timer activates when current time is within the threshold window before cancellableUntil.
 *
 * @example
 * // Timer active - shows countdown
 * {isTimerActive: true, timer: {hours: 2, minutes: 30, seconds: 45}}
 *
 * @example
 * // Timer inactive - either too early or deadline passed
 * {isTimerActive: false}
 */
export type CancellationTimer =
  | {
      /** Timer is active - within threshold window before deadline */
      isTimerActive: true
      /** Remaining time until cancellation deadline */
      timer: {
        /** Hours remaining (0-23) */
        hours: number
        /** Minutes remaining (0-59) */
        minutes: number
        /** Seconds remaining (0-59) */
        seconds: number
      }
    }
  | {
      /** Timer is not active - outside threshold window */
      isTimerActive: false
    }

/**
 * Discriminated union representing whether an order can be cancelled.
 * When cancellable, provides the UTC timestamp deadline.
 *
 * @example
 * // Order can be cancelled
 * {isCancellable: true, cancellableUntil: '2025-11-25T15:00:00Z'}
 *
 * @example
 * // Order cannot be cancelled (deadline passed or not set)
 * {isCancellable: false}
 */
export type CancellationStatus =
  | {
      /** Order can be cancelled */
      isCancellable: true
      /** UTC timestamp until which order can be cancelled (ISO 8601 format) */
      cancellableUntil: string
    }
  | {
      /** Order cannot be cancelled */
      isCancellable: false
    }

/**
 * Combined context props providing both cancellation status and timer state.
 * This is the complete state available to consumers of the OrderCancellationContext.
 *
 * @example
 * // Cancellable with active timer
 * {
 *   order: {...},
 *   isCancellable: true,
 *   cancellableUntil: '2025-11-25T15:00:00Z',
 *   isTimerActive: true,
 *   timer: {hours: 2, minutes: 30, seconds: 0},
 *   submit: (handleRequest) => {...}
 * }
 *
 * @example
 * // Cancellable but timer not yet active
 * {
 *   order: {...},
 *   isCancellable: true,
 *   cancellableUntil: '2025-11-25T15:00:00Z',
 *   isTimerActive: false,
 *   submit: (handleRequest) => {...}
 * }
 *
 * @example
 * // Not cancellable (deadline passed)
 * {
 *   order: {...},
 *   processData: {...},
 *   isCancellable: false,
 *   isTimerActive: false,
 *   submit: (handleRequest) => {...}
 * }
 */
export type OrderCancellationContextProps = {
  /**
   * Process-specific order data
   */
  processData: OrderDto
  /**
   * Current order data from backend
   * @deprecated Use processData instead. This field will be removed in a future version.
   */
  order: OrderDto
  /**
   * Submit handler that creates cancellation request and calls the provided handler.
   *
   * @template T - Return type of the handler function
   * @param handleRequest - Function that receives the order ID and cancellation request payload and performs the API call
   * @returns The result from the handleRequest function
   *
   * @example
   * const {submit} = useOrderCancellationContext()
   *
   * const cancelOrder = async () => {
   *   const result = await submit((orderId, request) => apiClient.cancelOrder(orderId, request))
   *   return result
   * }
   */
  submit: <T>(handleRequest: (orderId: string, request: OrderCancellationRequestPayload) => T) => T
} & CancellationStatus &
  CancellationTimer

/**
 * React context for order cancellation process.
 * Separate from OrderProcessContext to avoid shadowing when nested with OrderChangeContext.
 */
export const OrderCancellationProcessContext = createContext<
  OrderCancellationContextProps | undefined
>(undefined)
